import React from "react";
import "./App.css";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import Home from "./pages/Home/Home";
import Login from "./pages/Login/Login";
import Logout from "./pages/Logout/Logout";
import Register from "./pages/Register/Register";
import Store from "./pages/Store/Store";
import Recommendations from "./pages/Recommendations/Recommendations";
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import MyMachines from "./pages/MyMachines/MyMachines";
import { SiteProvider } from "./context/siteContext";
import Products from "./pages/Products/Products";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import { ThemeContextProvider } from "./context/ThemeContext";
import Settings from "./pages/Settings/Settings";
import BreadCrumbs from "./components/BreadCrumbs/BreadCrumbs";
import { CartProvider } from "./context/cartContext";
import Cart from "./pages/Cart/Cart";
import Checkout from "./pages/Checkout/Checkout";
import SingleProduct from "./pages/SingleProduct/SingleProduct";
import Profile from "./pages/Profile/Profile";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import AuthProvider from "./context/authContext";
import OptionsProvider from "./context/optionContext";
import ProtectedRoute from "./ProtectedRoute";
import OrderConfirmation from "./pages/OrderConfirmation/OrderConfirmation";
import Rspl from "./pages/Rspl/Rspl";
import OrderHistory from "./pages/OrderHistory/OrderHistory";
import Requests from "./pages/Requests/Requests";
import Documents from "./pages/Documents/Documents";
import SavedForLater from "./pages/SavedForLater/SavedForLater";
import PageNotFound from "./pages/PageNotFound/PageNotFound";
import Support from "./pages/Support/Support";
import ErrorBoundary from "./CentralError";
import Admin from "./pages/Admin/Admin";
import AcceptEULA from "./pages/AcceptEULA/AcceptEULA";
import VerifyEmail from "./pages/VerifyEmail/VerifyEmail";
import SpecialProtectedRoute from "./SpecialProtectedRoute";

const AppProviders = ({ children }) => (
	<ErrorBoundary>
		<OptionsProvider>
			<AuthProvider>
				<ThemeContextProvider>
					<SiteProvider>
						<CartProvider>{children}</CartProvider>
					</SiteProvider>
				</ThemeContextProvider>
			</AuthProvider>
		</OptionsProvider>
	</ErrorBoundary>
);

const AppRoutes = () => (
	<Routes>
		{/* Public Routes */}
		<Route path="/login" element={<Login />} />
		<Route path="/register" element={<Register />} />
		<Route path="/verify-email" element={<VerifyEmail />} />
		<Route path="/forgot-password" element={<ForgotPassword />} />
		<Route path="/reset-password" element={<ResetPassword />} />
		<Route path="/reset-password/:token" element={<ResetPassword />} />
		<Route path="*" element={<PageNotFound />} />

		{/* Protected Routes */}
		<Route element={<ProtectedRoute />}>
			<Route path="/accept-eula" element={<AcceptEULA />} />
			<Route element={<SpecialProtectedRoute />}>
				<Route path="/" element={<Home />} />
				<Route path="/home" element={<Home />} />
				<Route path="/machines" element={<MyMachines />} />
				<Route path="/machines/:machineID" element={<Products />} />
				<Route path="/machines/:machineID/:productID" element={<SingleProduct />} />
				<Route path="/my-lists" element={<Recommendations />} />
				<Route path="/cart" element={<Cart />} />
				<Route path="/cart/requests" element={<Cart openRequests={true} />} />
				<Route path="/checkout" element={<Checkout />} />
				<Route path="/quote-received/:orderKey" element={<OrderConfirmation />} />
				<Route path="/order-error/:orderKey" element={<OrderConfirmation />} />
				<Route path="/order-history" element={<OrderHistory />} />
				<Route path="/settings" element={<Settings />} />
				<Route path="/profile" element={<Profile />} />
				<Route path="/admin" element={<Admin />} />
				<Route path="/rspl" element={<Rspl />} />
				<Route path="/documents" element={<Documents />} />
				<Route path="/documents/:machine_id" element={<Documents />} />
				<Route path="/requests" element={<Requests />} />
				<Route path="/support" element={<Support />} />
				<Route path="/saved_for_later" element={<SavedForLater />} />
			</Route>
		</Route>

		{/* Logout Route */}
		<Route path="/logout" element={<Logout />} />
	</Routes>
);

const App = () => {
	const location = useLocation();
	const noHeaderFooterRoutes = [
		"/login",
		"/register",
		"/forgot-password",
		"/reset-password/*",
		"/reset-password",
		"/accept-eula",
		"/verify-email",
	];

	const isNoHeaderFooterRoute = noHeaderFooterRoutes.some((route) => {
		if (route === location.pathname) {
			return true;
		}

		// If route has a wildcard, use regex to match the pattern
		if (route.includes("/*")) {
			const baseRoute = route.replace("/*", "");
			const regex = new RegExp(`^${baseRoute}(/.*)?$`);
			return regex.test(location.pathname);
		}

		return false;
	});

	return (
		<AppProviders>
			<Box display="flex" flexDirection="column" minHeight="100vh" className="main-body">
				{!isNoHeaderFooterRoute && <Header />}

				{location.pathname !== "/home" && location.pathname !== "/" && !isNoHeaderFooterRoute && (
					<BreadCrumbs />
				)}
				<Container maxWidth={false} component="main" className="main-container">
					<AppRoutes />
				</Container>
				{!isNoHeaderFooterRoute && <Footer />}
				<ScrollToTop />
			</Box>
		</AppProviders>
	);
};



// Wrap App with Router to provide routing context
const AppWithRouter = () => (
	<Router>
		<App />
	</Router>
);

export default AppWithRouter;
